/**
 * File Name    : windowController.js
 * Authors      : Anders Hjelm, anders.hjelm@teliacompany.com
 *
 * Created      : 2017-11-22
 * Copyright    : Telia Sverige AB 2017
 *                Box 1218
 *                SE-751 42 Uppsala, Sweden
 *                The program may be used and/or copied only with the written
 *                permission from Telia Sverige AB, or in accordance with the
 *                terms and conditions stipulated in the agreement/contract
 *                under which the program has been supplied.
 *                All rights reserved.
 *
 * Purpose      : Only one window / tab at the time shall show video.
 *                This module controls that.
 ========================================================================== */

import { createLogger } from '@telia-ace/edge-log';
import { createUUID } from '@telia-ace/websdk-utilities';
import { registerXDEventHandler, setXDItem, getXDItem } from '@telia-ace/websdk-xd-storage';

const log = createLogger('chat-api/video/windowController');

/**
 * @module windowController
 *
 */

// Locally cached copy of this windows unique ID
let windowGUID;

// Get your ID. Locally cached, from XDStorage, or create a new ID
const getWindowGUID = async () => {
  if (!windowGUID) {
    const storeGUID = await getXDItem('CGWebSDK', 'session', 'windowGUID');
    if (storeGUID) {
      windowGUID = storeGUID;
    } else {
      windowGUID = createUUID();
      setXDItem('CGWebSDK', 'session', 'windowGUID', windowGUID);
    }
  }
  return windowGUID;
};

// There are cases where the window needs a new unique ID. E.g. if window.open is used,
// the new window will inherit the sessionStorage and hence also the ID.
export const setNewIdentifier = async () => {
  await unregisterVideoClient();
  windowGUID = createUUID();
  setXDItem('CGWebSDK', 'session', 'windowGUID', windowGUID);
};

// Declare that this window will show the video chat
export const iTakeIt = async () => {
  const GUID = await getWindowGUID();
  log.trace('This browser window takes over video chat!', GUID);
  setXDItem('CGWebSDK', 'local', 'videoShower', GUID);
};

// Ask if this window is the one showing the video chat
export const doIHaveIt = async () => {
  const GUID = await getWindowGUID();
  const showingGUID = await getXDItem('CGWebSDK', 'local', 'videoShower');
  log.trace('doIHaveIt', showingGUID === GUID, GUID, showingGUID);
  return showingGUID === GUID;
};

// When a video chat is finished, no one shall be the one showing video
export const noVideo = () => {
  log.trace('noVideo');
  setXDItem('CGWebSDK', 'local', 'videoShower', null);
};

// We would like to count the number of client windows. That is the only purpose with this function.
const registerVideoClient = async () => {
  const GUID = await getWindowGUID();
  log.trace('Registering videoClient ', GUID);
  let videoClients = await getXDItem('CGWebSDK', 'local', 'videoClients');
  videoClients = videoClients || [];
  videoClients.push(GUID);
  setXDItem('CGWebSDK', 'local', 'videoClients', videoClients);
};

// When a window closes, the number of video clients shall be reduced
async function unregisterVideoClient() {
  const GUID = await getWindowGUID();
  log.trace('Unregistering videoClient ', GUID);
  const videoClients = await getXDItem('CGWebSDK', 'local', 'videoClients');
  if (videoClients?.length) {
    for (let i = videoClients.length - 1; i >= 0; i--) {
      if (videoClients[i] === GUID) {
        videoClients.splice(i, 1);
      }
    }
    setXDItem('CGWebSDK', 'local', 'videoClients', videoClients);
  } else {
    setXDItem('CGWebSDK', 'local', 'videoClients', []);
  }
}

// How many clients do we have?
export const getNoOfVideoClients = async () => {
  const videoClients = await getXDItem('CGWebSDK', 'local', 'videoClients');
  return videoClients?.length || 0;
};

// The callback registered here will be called when an other window says it will show the video.
// This means that the present window shall stop showing video (if it is showing).
export const registerSomeoneElseTookIt = async (someOneElseTookItHandler) => {
  const GUID = await getWindowGUID();
  registerVideoClient();
  log.trace('Register someOneElseTookIt handler');
  registerXDEventHandler('CGWebSDK', (event) => {
    log.trace('Someone else might have taken video window! GUID, event: ', GUID, event);
    if (event.key === 'videoShower' && event.newValue !== null && event.newValue !== GUID) {
      log.trace('Yes, someone else took it. Call someOneElseTookItHandler()');
      if (typeof someOneElseTookItHandler === 'function') {
        someOneElseTookItHandler();
      }
    } else {
      log.trace('No, false alarm, video chat is still in same browser window.');
    }
  });
};

// Since things might crash and leave traces in local storage,
// clearUp is called at convenient places.
export const clearUp = async () => {
  setNewIdentifier();
  noVideo();
  setXDItem('CGWebSDK', 'local', 'videoClients', []);
  registerXDEventHandler('CGWebSDK', () => {});
};

// If this window goes away, it might be registered as a videoClient. Remove it from the list.
window.addEventListener('beforeunload', (ev) => {
  unregisterVideoClient();
  delete ev.returnValue;
  return undefined;
});
